import { useEffect, useRef, useState } from 'react';
import { Crop, ImagePlus, Loader2, Move, Trash2, ZoomIn } from 'lucide-react';
import { uploadMedia } from '../../lib/supabase';
import { BANNER_MAX_HOEHE, BANNER_MIN_HOEHE } from '../../lib/mailBuilder/render';
import type { BannerMeta } from '../../lib/mailBuilder/vorlagen';
import { Modal } from '../Modal/Modal';
import {
  MAIL_BREITE, ZOOM_MAX, ausschnitt, bannerZeichnen, begrenzen, bildLaden, hoeheBegrenzen, originalVorbereiten,
  type Zuschnitt,
} from './zuschnitt';
import './BannerEditor.css';

type Props = {
  id: string;
  /** Adresse des fertigen Banners */
  bild: string;
  meta: BannerMeta | null;
  onChange: (bild: string, meta: BannerMeta | null) => void;
};

type Bearbeitung = {
  /** blob: bei einem neuen Bild (wird erst beim Übernehmen hochgeladen), sonst die Adresse des Originals */
  quelle: string;
  datei: File | null;
  z: Zuschnitt;
  platz: 'oben' | 'unten';
};

/**
 * Bannerbild: hochladen, Ausschnitt verschieben und vergrößern, Höhe wählen
 * (höchstens 300 px in der Mail) und festlegen, ob es über oder unter dem
 * roten Kopf steht. Das Original bleibt gespeichert, damit sich der
 * Ausschnitt später ändern lässt.
 */
export function BannerEditor({ id, bild, meta, onChange }: Props) {
  const datei = useRef<HTMLInputElement>(null);
  const [bearbeitung, setBearbeitung] = useState<Bearbeitung | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const waehlen = (f: File | undefined) => {
    if (datei.current) datei.current.value = '';
    if (!f) return;
    setFehler(null);
    if (!f.type.startsWith('image/')) { setFehler('Bitte ein Bild wählen (JPG oder PNG).'); return; }
    if (f.size > 15 * 1024 * 1024) { setFehler('Das Bild ist größer als 15 MB — bitte vorher verkleinern.'); return; }
    setBearbeitung({
      quelle: URL.createObjectURL(f),
      datei: f,
      z: { x: 0.5, y: 0.5, zoom: 1, hoehe: meta?.hoehe ?? 280 },
      platz: meta?.platz ?? 'unten',
    });
  };

  const aendern = () => {
    setFehler(null);
    const quelle = meta?.quelle || bild;
    setBearbeitung({
      quelle,
      datei: null,
      z: { x: meta?.x ?? 0.5, y: meta?.y ?? 0.5, zoom: meta?.zoom ?? 1, hoehe: meta?.hoehe ?? 280 },
      platz: meta?.platz ?? 'unten',
    });
  };

  const schliessen = () => {
    if (bearbeitung?.quelle.startsWith('blob:')) URL.revokeObjectURL(bearbeitung.quelle);
    setBearbeitung(null);
  };

  const uebernehmen = async (b: Bearbeitung) => {
    const img = await bildLaden(b.quelle);
    let quelle = b.quelle;
    if (b.datei) {
      const original = await originalVorbereiten(b.datei);
      quelle = (await uploadMedia(original, 'einreichungen')).url;
    }
    let fertig: File;
    try {
      fertig = await bannerZeichnen(img, b.z);
    } catch {
      throw new Error('Dieses Bild lässt sich nicht zuschneiden. Bitte lade es noch einmal hoch.');
    }
    const url = (await uploadMedia(fertig, 'einreichungen')).url;
    onChange(url, { quelle, x: b.z.x, y: b.z.y, zoom: b.z.zoom, hoehe: hoeheBegrenzen(b.z.hoehe), platz: b.platz });
    schliessen();
  };

  return (
    <div className="be">
      {bild ? (
        <div className="be-vorschau">
          <img src={bild} alt="Bannerbild der Mail" style={{ aspectRatio: `${MAIL_BREITE} / ${meta?.hoehe ?? 280}` }} />
          <div className="be-aktionen">
            <span className="be-info">{meta ? `${meta.hoehe} px hoch · ${meta.platz === 'oben' ? 'über dem roten Kopf' : 'unter dem roten Kopf'}` : 'Bannerbild'}</span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={aendern}><Crop size={14} aria-hidden="true" /> Ausschnitt ändern</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => datei.current?.click()}><ImagePlus size={14} aria-hidden="true" /> Anderes Bild</button>
            <button type="button" className="btn btn-ghost btn-sm be-entfernen" onClick={() => onChange('', null)}><Trash2 size={14} aria-hidden="true" /> Entfernen</button>
          </div>
        </div>
      ) : (
        <button type="button" className="be-leer" onClick={() => datei.current?.click()}>
          <ImagePlus size={20} aria-hidden="true" />
          <span><strong>Bannerbild hochladen</strong><br />JPG oder PNG, am besten ein Foto im Querformat</span>
        </button>
      )}
      <input ref={datei} id={id} type="file" accept="image/*" hidden onChange={(e) => waehlen(e.target.files?.[0])} />
      {fehler && <p className="field-error" role="alert">{fehler}</p>}
      {bearbeitung && (
        <Zuschneiden
          start={bearbeitung}
          onAbbrechen={schliessen}
          onUebernehmen={uebernehmen}
        />
      )}
    </div>
  );
}

function Zuschneiden({ start, onAbbrechen, onUebernehmen }: {
  start: Bearbeitung;
  onAbbrechen: () => void;
  onUebernehmen: (b: Bearbeitung) => Promise<void>;
}) {
  const rahmen = useRef<HTMLDivElement>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [z, setZ] = useState<Zuschnitt>(start.z);
  const [platz, setPlatz] = useState(start.platz);
  const [breite, setBreite] = useState(MAIL_BREITE);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const ziehen = useRef<{ px: number; py: number; z: Zuschnitt } | null>(null);
  const [zieht, setZieht] = useState(false);

  useEffect(() => {
    let weg = false;
    bildLaden(start.quelle)
      .then((i) => { if (!weg) { setImg(i); setZ((alt) => begrenzen(i.naturalWidth, i.naturalHeight, alt)); } })
      .catch(() => { if (!weg) setFehler('Das Bild ließ sich nicht laden. Bitte lade es noch einmal hoch.'); });
    return () => { weg = true; };
  }, [start.quelle]);

  useEffect(() => {
    const el = rahmen.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBreite(el.clientWidth || MAIL_BREITE));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const setzen = (neu: Partial<Zuschnitt>) => {
    setZ((alt) => (img ? begrenzen(img.naturalWidth, img.naturalHeight, { ...alt, ...neu }) : { ...alt, ...neu }));
  };

  // Mausrad zum Vergrößern — als eigener Listener, damit die Seite nicht mitscrollt.
  useEffect(() => {
    const el = rahmen.current;
    if (!el) return;
    const rad = (e: WheelEvent) => {
      e.preventDefault();
      setZ((alt) => (img ? begrenzen(img.naturalWidth, img.naturalHeight, { ...alt, zoom: alt.zoom * (e.deltaY < 0 ? 1.08 : 1 / 1.08) }) : alt));
    };
    el.addEventListener('wheel', rad, { passive: false });
    return () => el.removeEventListener('wheel', rad);
  }, [img]);

  const hoeheAnzeige = (breite * hoeheBegrenzen(z.hoehe)) / MAIL_BREITE;
  const a = img ? ausschnitt(img.naturalWidth, img.naturalHeight, z) : null;
  const massstab = a ? breite / a.sb : 1;

  const verschieben = (dx: number, dy: number, basis: Zuschnitt) => {
    if (!img || !a) return;
    const cx = basis.x * img.naturalWidth - dx / massstab;
    const cy = basis.y * img.naturalHeight - dy / massstab;
    setZ(begrenzen(img.naturalWidth, img.naturalHeight, { ...basis, x: cx / img.naturalWidth, y: cy / img.naturalHeight }));
  };

  const taste = (e: React.KeyboardEvent) => {
    const schritt = e.shiftKey ? 40 : 10;
    if (e.key === 'ArrowLeft') verschieben(schritt, 0, z);
    else if (e.key === 'ArrowRight') verschieben(-schritt, 0, z);
    else if (e.key === 'ArrowUp') verschieben(0, schritt, z);
    else if (e.key === 'ArrowDown') verschieben(0, -schritt, z);
    else if (e.key === '+' || e.key === '=') setzen({ zoom: z.zoom + 0.1 });
    else if (e.key === '-') setzen({ zoom: z.zoom - 0.1 });
    else return;
    e.preventDefault();
  };

  const fertig = async () => {
    setSpeichert(true);
    setFehler(null);
    try {
      await onUebernehmen({ ...start, z, platz });
    } catch (e) {
      setFehler((e as Error).message);
      setSpeichert(false);
    }
  };

  return (
    <Modal isOpen onClose={() => { if (!speichert) onAbbrechen(); }} title="Bannerbild zuschneiden" size="lg">
      <p className="be-anleitung">
        <Move size={15} aria-hidden="true" /> Ziehe das Bild an die richtige Stelle, mit dem Regler oder dem Mausrad
        vergrößerst Du. Der Rahmen zeigt genau, was in der Mail zu sehen ist.
      </p>
      <div
        ref={rahmen}
        className={`be-rahmen${zieht ? ' be-rahmen--zieht' : ''}`}
        style={{ height: hoeheAnzeige }}
        tabIndex={0}
        role="group"
        aria-label="Ausschnitt: ziehen oder mit den Pfeiltasten verschieben, mit Plus und Minus vergrößern"
        onKeyDown={taste}
        onPointerDown={(e) => {
          if (!img) return;
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          ziehen.current = { px: e.clientX, py: e.clientY, z };
          setZieht(true);
        }}
        onPointerMove={(e) => {
          const s = ziehen.current;
          if (!s) return;
          verschieben(e.clientX - s.px, e.clientY - s.py, s.z);
        }}
        onPointerUp={() => { ziehen.current = null; setZieht(false); }}
        onPointerCancel={() => { ziehen.current = null; setZieht(false); }}
      >
        {img && a ? (
          <img
            src={start.quelle}
            alt=""
            draggable={false}
            className="be-bild"
            style={{
              width: img.naturalWidth * massstab,
              height: img.naturalHeight * massstab,
              left: -a.sx * massstab,
              top: -a.sy * massstab,
            }}
          />
        ) : !fehler ? (
          <div className="be-laedt"><Loader2 size={20} className="spin" aria-hidden="true" /> Bild wird geladen …</div>
        ) : null}
      </div>

      <div className="be-regler">
        <label className="be-regler-zeile">
          <span><ZoomIn size={15} aria-hidden="true" /> Vergrößern</span>
          <input type="range" min={1} max={ZOOM_MAX} step={0.01} value={z.zoom} onChange={(e) => setzen({ zoom: Number(e.target.value) })} disabled={!img} />
          <span className="be-wert">{Math.round(z.zoom * 100)} %</span>
        </label>
        <label className="be-regler-zeile">
          <span>Höhe in der Mail</span>
          <input type="range" min={BANNER_MIN_HOEHE} max={BANNER_MAX_HOEHE} step={10} value={hoeheBegrenzen(z.hoehe)} onChange={(e) => setzen({ hoehe: Number(e.target.value) })} disabled={!img} />
          <span className="be-wert">{hoeheBegrenzen(z.hoehe)} px</span>
        </label>
        <fieldset className="be-platz">
          <legend>Platz in der Mail</legend>
          <label className={`chip${platz === 'unten' ? ' active' : ''}`}>
            <input type="radio" name="banner-platz" value="unten" checked={platz === 'unten'} onChange={() => setPlatz('unten')} /> Unter dem roten Kopf
          </label>
          <label className={`chip${platz === 'oben' ? ' active' : ''}`}>
            <input type="radio" name="banner-platz" value="oben" checked={platz === 'oben'} onChange={() => setPlatz('oben')} /> Über dem roten Kopf
          </label>
        </fieldset>
        <p className="form-hint">Höchstens {BANNER_MAX_HOEHE} Pixel hoch, damit auf dem Handy der Anfang der Mail sichtbar bleibt.</p>
      </div>

      {fehler && <p className="field-error" role="alert">{fehler}</p>}
      <div className="be-fuss">
        <button type="button" className="btn btn-secondary" onClick={onAbbrechen} disabled={speichert}>Abbrechen</button>
        <button type="button" className="btn btn-primary" onClick={fertig} disabled={!img || speichert}>
          {speichert ? <><Loader2 size={16} className="spin" aria-hidden="true" /> Wird gespeichert …</> : <><Crop size={16} aria-hidden="true" /> Übernehmen</>}
        </button>
      </div>
    </Modal>
  );
}
